import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const Immigration = ({ onGetStarted }) => {
  const faqs = [
    { question: 'What is an H-1B visa?', answer: 'The H-1B is a temporary work visa for specialty occupations requiring specialized knowledge and a bachelor\'s degree or higher. It is employer-sponsored and initially valid for up to three years, extendable to six years.' },
    { question: 'How long does the green card process take?', answer: 'Timeline varies by category and country of birth. Employment-based green cards typically take 1-3 years for labor certification and I-140 petition, plus additional time for visa availability and adjustment of status.' },
    { question: 'What is labor certification (PERM)?', answer: 'PERM is the process where employers demonstrate no qualified U.S. workers are available for a position before sponsoring a foreign worker for a green card. It involves recruitment, prevailing wage determination, and DOL filing.' },
    { question: 'Can my company transfer employees from overseas offices?', answer: 'Yes, through L-1 intracompany transfer visas. L-1A is for managers/executives and L-1B is for employees with specialized knowledge. The employee must have worked for the company abroad for at least one continuous year.' },
    { question: 'What is I-9 compliance?', answer: 'I-9 compliance requires employers to verify employment authorization of all employees. Employers must complete Form I-9 for each employee and maintain records. Violations can result in significant fines and sanctions.' },
  ];

  return (
    <div className="pt-20">
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/6077326/pexels-photo-6077326.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Compliance & Protection Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Immigration Law</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Business immigration, work visas, and employment authorization. Help your company hire and retain global talent.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">Business immigration law enables companies to hire and retain international talent. We handle all aspects of employment-based immigration including work visas, green cards, and compliance matters.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {['H-1B, L-1, and other work visas', 'Permanent residence (green card) applications', 'Labor certification (PERM)', 'I-9 compliance and audits', 'Immigration policy development', 'Deportation defense and appeals'].map((benefit, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1655722724135-eb9216c95618?w=800&q=80" alt="Immigration visa services" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Visa & Immigration Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {['H-1B Visas', 'L-1 Intracompany Transfers', 'E-2 Treaty Investors', 'O-1 Extraordinary Ability', 'TN NAFTA Professionals', 'Green Cards (EB-1, EB-2, EB-3)', 'Labor Certification (PERM)', 'I-9 Compliance'].map((visa, i) => (
              <div key={i} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{visa}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {[
              { title: 'Case Evaluation', description: 'Assess eligibility and determine optimal immigration strategy.' },
              { title: 'Document Preparation', description: 'Prepare comprehensive petitions and supporting evidence.' },
              { title: 'Government Filing', description: 'Submit applications to USCIS, DOL, or State Department.' },
              { title: 'Case Management', description: 'Monitor case status and respond to government requests.' },
            ].map((step, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{i + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1487637419635-a2a471ff5c7b?w=800&q=80" alt="Why choose our immigration team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {[
                  { icon: Award, title: 'Immigration Expertise', description: 'Extensive experience with business immigration matters.' },
                  { icon: Shield, title: 'High Success Rate', description: 'Proven track record of visa and green card approvals.' },
                  { icon: Clock, title: 'Efficient Processing', description: 'Strategic approach to minimize processing delays.' },
                  { icon: TrendingUp, title: 'Comprehensive Support', description: 'End-to-end immigration services for businesses.' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/3184639/pexels-photo-3184639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Need Immigration Assistance?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for business immigration legal services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default Immigration;
